import { CalendarOutlined, TableOutlined } from "@ant-design/icons";
import { Button, Col, Empty, Modal, Row, Table, Typography } from "antd";
import moment from "moment";
import { useEffect, useState } from "react";
import { Calendar, momentLocalizer, Views } from "react-big-calendar";
import { useParams } from "react-router-dom";
import SecondaryButton from "../Buttons/SecondaryButton";
import { HOW_DO_YOU_FEEL_OPTIONS } from "./constants";
import { createCommonTableColumns, formatCalendarEventTime } from "./helpers";
import CalendarToolbar from "./Views/CalendarToolbar";

const localizer = momentLocalizer(moment);

import { translate } from "../../locale";
import LogBookServices from "../../services/LogBookServices";
import UserServices from "../../services/UserServices";

const { Title, Text } = Typography;

const BreathingCalendar = () => {
  const [data, setData] = useState(null);
  const [date, setDate] = useState(moment().format("YYYY-MM-DD"));
  const [patient, setPatient] = useState(null);
  const [dataTable, setDataTable] = useState(null);
  const [modalDetails, setModalDetails] = useState(null);
  const [showAs, setShowAs] = useState("calendar");

  const { username } = useParams();

  useEffect(() => {
    loadPatientInfo();
  }, []);

  const loadPatientInfo = async () => {
    try {
      const res = await UserServices.getProfile(username);
      if (res.status >= 200 && res.status < 300) {
        setPatient(res.data.my_contact_info);
      }
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    fetchData();
  }, [date]);

  const fetchData = async () => {
    try {
      const response = await LogBookServices.getBreathing(username, date);
      if (
        response.status >= 200 &&
        response.status <= 300 &&
        response.data.breathing_cycle.length !== 0
      ) {
        let newData = response.data.breathing_cycle.map((i) => {
          const registerDate = i.register_date;
          const registerDatePlusOne = new Date(registerDate);
          registerDatePlusOne.setHours(registerDatePlusOne.getHours() + 1);

          return {
            minutes: i.minutes,
            como_te_fue: i.como_te_fue,
            difficulty: i.difficulty,
            start: new Date(i.register_date),
            end: registerDatePlusOne,
            cycles: i.cycles,
            type: "cell",
            formattedRegisterDate: moment(i.register_date).format("HH:mm:ss"),
          };
        });

        setDataTable(response.data.breathing_cycle);
        setData(newData);
      }
    } catch (error) {
      console.error(error);
    }
  };

  const handleDetails = (item) => {
    setModalDetails(item);
  };

  const columns = createCommonTableColumns(handleDetails);

  const handleHideDetails = () => {
    setModalDetails(null);
  };

  const handlePrev = () => {
    setDate((v) => moment(v).subtract(7, "days").format("YYYY-MM-DD"));
  };
  const handleToday = () => {
    setDate((v) => moment().format("YYYY-MM-DD"));
  };
  const handleNext = () => {
    if (date !== moment().format("YYYY-MM-DD")) {
      setDate((v) => moment(v).add(7, "days").format("YYYY-MM-DD"));
    }
  };

  const calendarWeekEvent = ({ event }) => {
    return (
      <button
        className={`text-center w-full ${
          event.type === "cell" ? "" : "rbc-event-content-header"
        }`}
        onClick={(e) => {
          e.preventDefault();
          if (event.type === "cell") {
            handleDetails(event);
          }
        }}
      >
        {formatCalendarEventTime(event)}
      </button>
    );
  };

  const getCalendarToolbar = (props) => {
    return (
      <CalendarToolbar
        {...props}
        onPrev={handlePrev}
        onToday={handleToday}
        onNext={handleNext}
      />
    );
  };

  return (
    <Row className="w-full">
      <Col flex="column" className="w-full" id="Breathing">
        <Row className="w-full">
          <Col span={24} className="mt-4">
            <Title level={2} className="text-center">
              {translate("Respiración de ")}
              {patient && `${patient.user.full_name}`}
            </Title>
          </Col>
        </Row>
        <Row className="mt-4">
          <Col span={24} className="flex flex-row justify-end space-x-2">
            <span className="flex flex-col justify-center">
              {translate("Formato")}:
            </span>
            <Button
              icon={<CalendarOutlined />}
              onClick={() => setShowAs("calendar")}
              type={showAs === "calendar" ? "primary" : "default"}
            />
            <Button
              icon={<TableOutlined />}
              onClick={() => setShowAs("table")}
              type={showAs === "table" ? "primary" : "default"}
            />
          </Col>
        </Row>
        {showAs === "calendar" && (
          <Calendar
            localizer={localizer}
            defaultDate={null}
            defaultView={Views.WEEK}
            views={["week"]}
            events={data || []}
            startAccessor="start"
            endAccessor="end"
            step={60}
            timeslots={6}
            style={{ height: 500 }}
            components={{
              toolbar: (props) => getCalendarToolbar(props),
              week: {
                event: calendarWeekEvent,
              },
            }}
            formats={{
              eventTimeRangeFormat: () => {
                return "";
              },
              timeGutterFormat: () => {
                return "";
              },
            }}
          />
        )}
        {showAs === "table" && (
          <>
            <div className="flex flex-row my-4">
              <div>
                <SecondaryButton
                  className="mr-4"
                  onClick={() => {
                    handlePrev();
                  }}
                >
                  &lt;
                </SecondaryButton>
                <SecondaryButton
                  className="mr-4"
                  onClick={() => {
                    handleToday();
                  }}
                >
                  Hoy
                </SecondaryButton>
                <SecondaryButton
                  className=""
                  onClick={() => {
                    handleNext();
                  }}
                >
                  &gt;
                </SecondaryButton>
              </div>
            </div>
            <Table
              rowKey={(i) => i.id}
              className="w-full mt-6"
              dataSource={dataTable}
              columns={columns}
              locale={{
                emptyText: <Empty description={translate("No hay datos")} />,
                sortTitle: translate("Ordenar"),
                triggerDesc: translate("Ordenar (descendente)"),
                triggerAsc: translate("Ordenar (ascendente)"),
                cancelSort: translate("Cancelar orden"),
                filterTitle: translate("Filtros"),
                filterConfirm: translate("Aceptar"),
                filterReset: translate("Limpiar"),
                filterEmptyText: translate("No hay filtros"),
                filterCheckall: translate("Seleccinar todos"),
                filterSearchPlaceholder: translate("Buscar en filtros"),
              }}
            />
          </>
        )}
        <Modal
          width={600}
          open={modalDetails !== null}
          title={translate("Detalles del entrenamiento")}
          onCancel={handleHideDetails}
          footer={[
            <Button key="ok" type="primary" onClick={handleHideDetails}>
              {translate("Aceptar")}
            </Button>,
          ]}
        >
          {modalDetails !== null && (
            <Row>
              <Col className="flex-1">
                <Row className="mt-4">
                  <Col>
                    <Text>Minutos: {modalDetails.minutes}</Text>
                  </Col>
                </Row>
                <Row className="pb-4">
                  <Col span={14}></Col>
                </Row>
                <Row className="mt-3">
                  <Col>
                    <Text>
                      ¿Cómo te fue?:{" "}
                      {HOW_DO_YOU_FEEL_OPTIONS.find(
                        (item) => item.label === modalDetails.como_te_fue
                      )?.value || modalDetails.como_te_fue}
                    </Text>
                  </Col>
                </Row>
                <Row>
                  <Col>
                    <Text>
                      Registrado a las {modalDetails.formattedRegisterDate}
                    </Text>
                  </Col>
                </Row>
                <Row>
                  <Col>
                    <Text>Dificultad: {modalDetails.difficulty}</Text>
                  </Col>
                </Row>
                <Row>
                  <Col>
                    <Text>Ciclos: {modalDetails.cycles}</Text>
                  </Col>
                </Row>
              </Col>
            </Row>
          )}
        </Modal>
      </Col>
    </Row>
  );
};

export default BreathingCalendar;
